import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ConsentField, Field, FormError, Honeypot, Segmented, SelectInput, SubmitButton, TextInput } from "@/components/inquiry/fields";
import { PhotoDrop } from "@/components/inquiry/PhotoDrop";
import { damageSchema } from "@/components/inquiry/schemas";
import { useLeadSubmit } from "@/components/inquiry/useLeadSubmit";
import { CAUSES } from "@/lib/leadLabels";
import { cn } from "@/lib/utils";

const DRIVABLE = [{ value: "ja", label: "Ja" }, { value: "nein", label: "Nein" }];

export const DamageForm = ({ prefix = "damage", wide = false }) => {
  const [photos, setPhotos] = useState([]);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(damageSchema),
    mode: "onTouched",
    defaultValues: { name: "", phone: "", email: "", cause: "", plz: "", plate: "", accident_date: "", drivable: null, opponent_insurance: "", consent: false, website: "" },
  });
  const { submit, progress, error } = useLeadSubmit("damage");
  const pair = cn("grid gap-4", wide ? "sm:grid-cols-2" : "grid-cols-2");
  const today = new Date().toISOString().slice(0, 10);

  return (
    <form onSubmit={handleSubmit((v) => submit(v, photos))} noValidate className="relative space-y-4" data-testid={`${prefix}-form`}>
      <Honeypot register={register} />
      <div className={cn("grid gap-4", wide && "sm:grid-cols-2")}>
        <Field label="Name" required htmlFor={`${prefix}-name`} error={errors.name?.message} className={wide ? "sm:col-span-2" : ""}>
          <TextInput id={`${prefix}-name`} autoComplete="name" invalid={!!errors.name} {...register("name")} data-testid={`${prefix}-name`} />
        </Field>
        <Field label="Telefon" required htmlFor={`${prefix}-phone`} error={errors.phone?.message}>
          <TextInput id={`${prefix}-phone`} type="tel" inputMode="tel" autoComplete="tel" invalid={!!errors.phone} {...register("phone")} data-testid={`${prefix}-phone`} />
        </Field>
        <Field label="E-Mail" required htmlFor={`${prefix}-email`} error={errors.email?.message}>
          <TextInput id={`${prefix}-email`} type="email" autoComplete="email" invalid={!!errors.email} {...register("email")} data-testid={`${prefix}-email`} />
        </Field>
      </div>
      <Field label="Schadenursache" required htmlFor={`${prefix}-cause`} error={errors.cause?.message}>
        <SelectInput id={`${prefix}-cause`} invalid={!!errors.cause} {...register("cause")} data-testid={`${prefix}-cause`}>
          <option value="">Bitte wählen</option>
          {CAUSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </SelectInput>
      </Field>
      <div className={pair}>
        <Field label="PLZ Fahrzeugstandort" required htmlFor={`${prefix}-plz`} error={errors.plz?.message}>
          <TextInput id={`${prefix}-plz`} inputMode="numeric" maxLength={5} autoComplete="postal-code" placeholder="50733" invalid={!!errors.plz} {...register("plz")} data-testid={`${prefix}-plz`} />
        </Field>
        <Field label="Kennzeichen" htmlFor={`${prefix}-plate`} error={errors.plate?.message}>
          <TextInput id={`${prefix}-plate`} placeholder="K-AB 1234" className="uppercase" invalid={!!errors.plate} {...register("plate")} data-testid={`${prefix}-plate`} />
        </Field>
      </div>
      <div className={pair}>
        <Field label="Unfalldatum" htmlFor={`${prefix}-date`}>
          <TextInput id={`${prefix}-date`} type="date" max={today} {...register("accident_date")} data-testid={`${prefix}-date`} />
        </Field>
        <fieldset className="space-y-1.5">
          <legend className="mb-1.5 text-[13px] font-semibold text-brand-anthracite">Fahrbereit? <span className="font-normal text-slate-500">(optional)</span></legend>
          <Segmented name="drivable" options={DRIVABLE} register={register} prefix={`${prefix}-drivable`} cols={2} />
        </fieldset>
      </div>
      <Field label="Gegnerische Versicherung" htmlFor={`${prefix}-insurance`} error={errors.opponent_insurance?.message}>
        <TextInput id={`${prefix}-insurance`} placeholder="falls bekannt" invalid={!!errors.opponent_insurance} {...register("opponent_insurance")} data-testid={`${prefix}-insurance`} />
      </Field>
      <div className="space-y-1.5">
        <p className="text-[13px] font-semibold text-brand-anthracite">Schadensfotos <span className="font-normal text-slate-500">(optional)</span></p>
        <PhotoDrop files={photos} onChange={setPhotos} prefix={prefix} />
      </div>
      <ConsentField id={`${prefix}-consent`} register={register} error={errors.consent?.message} />
      <FormError error={error} testId={`${prefix}-submit-error`} />
      <SubmitButton loading={isSubmitting} progress={progress} testId={`${prefix}-submit`}>Schaden jetzt melden</SubmitButton>
    </form>
  );
};
